import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import { AlertCircle, UserCircle, UserRoundPenIcon } from "lucide-react";
import { toast } from "sonner";
import { useOrganizationSettingsStore } from "@/stores";
import { useAdminRole } from "@/hooks/IsAdminContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle, ItemList } from "@/components/ui/item";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Whose Dub account mints the affiliate links (organizations.dub_user_id).
 * Members connect Dub under Personal › Connected accounts; admins pick one
 * of them here.
 */
export function DubIntegrationFrame() {
  const { organizationInfo, dubUsers, fetchDubUsers, saveOrganizationSetting } =
    useOrganizationSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();
  const { isAdmin } = useAdminRole();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadDubUsers = async () => {
      if (orgId) {
        setLoading(true);
        try {
          const token = await getToken();
          if (token) {
            await fetchDubUsers(orgId, token);
          }
        } finally {
          setLoading(false);
        }
      }
    };
    loadDubUsers();
  }, [orgId, getToken, fetchDubUsers]);

  const handleSelect = async (userId: string) => {
    if (!orgId || !user?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const success = await saveOrganizationSetting(
        orgId,
        user.id,
        "organization_dub_user_id",
        userId,
        token,
      );
      if (success) {
        toast.success("Dub user updated successfully");
        setPickerOpen(false);
      } else {
        toast.error("Failed to update Dub user");
      }
    } catch {
      toast.error("Failed to update Dub user");
    } finally {
      setSaving(false);
    }
  };

  const selected = dubUsers.find((u) => u.user_id === organizationInfo?.dub_user_id);

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Dub affiliate links</FrameTitle>
        <FrameDescription>Whose Dub account creates affiliate links.</FrameDescription>
      </FrameHeader>

      {loading ? (
        <FramePanel className="p-3">
          <Skeleton className="h-9 w-full" />
        </FramePanel>
      ) : dubUsers.length === 0 ? (
        <Alert variant="warning">
          <AlertTitle>
            <AlertCircle className="size-4" />
            No Dub accounts connected
          </AlertTitle>
          <AlertDescription>Connect Dub under Personal › Connected accounts first.</AlertDescription>
        </Alert>
      ) : (
        <ItemList>
          <Item variant="row">
            <ItemMedia>
              {selected ? (
                <Avatar variant="square">
                  <AvatarImage src={selected.imageUrl || ""} />
                  <AvatarFallback>{selected.name ? selected.name.charAt(0) : "U"}</AvatarFallback>
                </Avatar>
              ) : (
                <div className="flex size-8 items-center justify-center rounded-md border bg-muted-foreground/10">
                  <UserCircle className="size-4 text-muted-foreground" />
                </div>
              )}
            </ItemMedia>
            <ItemContent className="min-w-0 gap-0">
              <ItemTitle>{selected ? selected.name : "No user selected"}</ItemTitle>
              <ItemDescription className="truncate">
                {selected ? selected.email : "Select a Dub user for affiliate links"}
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPickerOpen(true)}
                disabled={!isAdmin}
              >
                <UserRoundPenIcon />
                {selected ? "Change Dub User" : "Select Dub User"}
              </Button>
            </ItemActions>
          </Item>
        </ItemList>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Dub User</DialogTitle>
            <DialogDescription>
              Choose which user's Dub account to use for generating affiliate
              links and partners tracking.
            </DialogDescription>
          </DialogHeader>
          <SelectList
            value={organizationInfo?.dub_user_id ?? ""}
            onValueChange={handleSelect}
            disabled={saving}
            className="max-h-100"
          >
            {dubUsers.map((dubUser) => (
              <SelectListItem key={dubUser.user_id} value={dubUser.user_id}>
                <ItemMedia>
                  <Avatar variant="square">
                    <AvatarImage src={dubUser.imageUrl || ""} />
                    <AvatarFallback>{dubUser.name ? dubUser.name.charAt(0) : "U"}</AvatarFallback>
                  </Avatar>
                </ItemMedia>
                <ItemContent className="min-w-0 gap-0">
                  <ItemTitle>{dubUser.name}</ItemTitle>
                  <ItemDescription className="truncate">{dubUser.email}</ItemDescription>
                </ItemContent>
              </SelectListItem>
            ))}
          </SelectList>
        </DialogContent>
      </Dialog>
    </Frame>
  );
}
